import React, { useState, useCallback, useMemo, useRef } from "react";
import Calendar from "../../../components/Calendar/Calendar";
import WidgetCard, { WidgetCardProps } from "../../../components/WidgetCard/WidgetCard";
import { MachineTest } from "../../../../models/machine-test.model";
import { MachineTestResult } from "../../../../models/machine-test-result.model";
import moment from "moment";
import { useAppContext } from "../../../../context/app.context";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import environment from "../../../../env";
import { useHistory } from "react-router-dom";
import { Drawer, Menu, MenuItem, ListItemIcon, ListItemText, Divider, Grid, Link, Box } from "@material-ui/core";
import PageContainer from "../../../components/Page/PageContainer";
import TestList from "../../../components/TestList/TestList";
import { Close, HelpOutline, FilterList, MoreVert, RemoveCircleOutline } from "@material-ui/icons";
import { MachineEvent } from "../../../../models/machine-event.model";
import { MachineLog } from "../../../../models/machine-log.model";
import useMachineService from "../../../../hooks/machine-service.hook";
import { EventSearchParams } from "../../../../models/event-search-params.model";
import TestResultList from "../../../components/TestResultList/TestResultList";
import PageLoader from "../../../components/PageLoader/PageLoader";
import IconButton from "../../../components/IconButton/IconButton";
import { useUserContext } from "../../../../context/user.context";
import useMachineLogService from "../../../../hooks/machine-log-service.hook";
import useMachineTestResultService from "../../../../hooks/machine-test-result-service.hook";
import notifications from "../../../../translations/en/notifications.json";
import { Permission } from "../../../../models/group.model";
import RetryWidget from "../../../components/ImageCaptions/RetryWidget";
import { useQuery } from "react-query";
import usePersistedState from "../../../../hooks/persisted-state.hook";
import Button from "../../../components/Button/Button";
import { useStatusEffect } from "../../../../hooks/status-effect.hook";
import { CalendarLegendPopup } from "../../../components/Calendar/CalendarLegendPopup";
import { CALENDAR_STATUS_OPTIONS } from "../../../components/Calendar/calendarStatusConfig";
import useDeviceTestService from "../../../../hooks/device-test-service.hook";
import useSearchbarService from "../../../../hooks/searchbar-service.hook";
import { StatusType } from "../../../../constants/status";
import { TestRunStatus } from "../../../../models/test-run.model";

const CALENDAR_TEST_LIST_HASH = "#calendar-test-list";
const CALENDAR_TEST_RESULT_LIST_HASH = "#calendar-test-result-list";
const DATE_FORMAT = "YYYY-MM-DD";

interface CalendarOptions {
  criteria: EventSearchParams;
  showTests: boolean;
  showSchedule: boolean;
  showLogs: boolean;
  showTestResults: boolean;
}

const defaultCalendarOptions: CalendarOptions = {
  showTests: true,
  showSchedule: true,
  showLogs: true,
  showTestResults: true,
  criteria: {
    start: moment().startOf("month").unix(),
    end: moment().endOf("month").unix(),
    daily: true,
    weekly: true,
    biweekly: true,
    biannual: true,
    monthly: true,
    quarterly: true,
    semiannual: true,
    yearly: true,
    ready: 1,
    itemsPerPage: 1000,
  },
};

export interface CalendarWidgetProps extends WidgetCardProps {
  onRemoveWidget?: () => void;
}

function CalendarWidget(props: CalendarWidgetProps) {
  const { setAlert } = useAppContext();
  const { org, user, hasPermission, facility, machine } = useUserContext();
  const { getDeviceTests, getCalendarCompliance, getWidgetTestResults, getDeviceTestResults } = useDeviceTestService();
  const { approveTestResultsV2, signTestResultsV2 } = useMachineTestResultService();
  const { getMachineSchedule } = useMachineService();
  const { getMachineLogsV2 } = useMachineLogService();


  const history = useHistory();

  const [retry, setRetry] = useState(0);
  const [loading, setLoading] = useState(false);
  // Loader for the calendar's test / test-result list drawers while their rows are fetched.
  const [listLoading, setListLoading] = useState(false);
  // Latest drawer request, so a slow response for a previously clicked row is ignored.
  const listRequestId = useRef(0);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const [selectedDate, setSelectedDate] = usePersistedState<string>("home/widget/calendar/selected-date", {
    defaultValue: moment().format(DATE_FORMAT),
  });

  const [selectedTests, setSelectedTests] = usePersistedState<{ name: string; tests: MachineTest[]; statuses?: Record<string, StatusType> } | undefined>(
    "home/widget/calendar/selected-tests"
  );

  const [selectedTestResults, setSelectedTestResults] = usePersistedState<{ name: string; testResults: MachineTestResult[] } | undefined>(
    "home/widget/calendar/selected-test-results"
  );

  const [calendarOptions, setCalendarOptions] = usePersistedState<CalendarOptions>("home/widget/calendar/options", {
    defaultValue: defaultCalendarOptions,
  });

  const [showCalendarTestList, setShowCalendarTestList] = useState(() => {
    // Need to grab the initial state outside of history listen bc that is only
    // fired on a change and does not include initial route.
    return history.location.hash === CALENDAR_TEST_LIST_HASH;
  });

  const [showCalendarTestResultList, setShowCalendarTestResultList] = useState(() => {
    // returning false on initial load so the test reuslt list goes away when you change orgs.
    // it gets set below when you click results
    return false;
  });

  const options = { ...defaultCalendarOptions, ...calendarOptions };
  const [inputCriteria, setInputCriteria] = useState<CalendarOptions & { delay: number }>({ ...options, delay: 0 });
  const { searchbarFilter } = useSearchbarService();

  // Toggle the test or test result list.
  // Need access to status outside of effect.
  const { status } = useStatusEffect(
    (status) => {
      // By returning a function the function will be called on unmount
      const listener = history.listen((route) => {
        if (status.current !== "unmount") {
          // We need to listen for back button press to close calendar test list
          if (route.hash !== CALENDAR_TEST_LIST_HASH && showCalendarTestList) {
            setShowCalendarTestList(false);
          } else if (route.hash === CALENDAR_TEST_LIST_HASH && !showCalendarTestList) {
            setShowCalendarTestList(true);
          }

          if (route.hash !== CALENDAR_TEST_RESULT_LIST_HASH && showCalendarTestResultList) {
            setShowCalendarTestResultList(false);
          } else if (route.hash === CALENDAR_TEST_RESULT_LIST_HASH && !showCalendarTestResultList) {
            setShowCalendarTestResultList(true);
          }
        }
      });

      return listener;
    },
    [history, showCalendarTestList, showCalendarTestResultList, setShowCalendarTestList, setShowCalendarTestResultList]
  );

  // Get the data for this widget

  const {
    isLoading: testsLoading,
    isError: testsError,
    data: tests,
  } = useQuery(["home/widget/calendar/tests", searchbarFilter, org, retry], {
    queryFn: async () => {
      if (!options.showTests || !searchbarFilter || !org) {
        return Promise.resolve({ data: [], total: 0 });
      }

      options.criteria.facilityId = searchbarFilter.facilityId;
      options.criteria.machineId = searchbarFilter.machineId;
      const orgId = org ? org.id : searchbarFilter.orgId!;
      const result = await getDeviceTests(orgId, options.criteria);

      // Not filtered by perform permission, so view-only groups (e.g. Reviewer) can see tests read-only.
      // Running is still gated per test by disableRunTestIds.
      return {
        data: result.items,
        total: result.total
      };
    },
    refetchOnMount: 'always',
  });

  const {
    isLoading: complianceGridLoading,
    data: complianceGrid,
  } = useQuery(["home/widget/calendar/compliance-grid", options.criteria.start, options.criteria.end, searchbarFilter, org, retry], {
    queryFn: async () => {
      if (!options.showTests || !searchbarFilter || !org) {
        return Promise.resolve([]);
      }
      // A month that starts after today has no closed compliance window (the API clamps the grid
      // to yesterday and would return []), so skip the request.
      if (options.criteria.start != null && moment.unix(options.criteria.start).isAfter(moment().endOf("day"))) {
        return Promise.resolve([]);
      }
      // Not filtered by perform permission, so view-only groups (e.g. Reviewer) still see compliance.
      return await getCalendarCompliance(org.id, {
        facilityId: searchbarFilter.facilityId,
        machineId: searchbarFilter.machineId,
        start: options.criteria.start,
        end: options.criteria.end,
      });
    },
    refetchOnMount: "always",
  });

  const {
    isLoading: logsLoading,
    isError: logsError,
    data: logs,
  } = useQuery(["home/widget/calendar/logs", options, searchbarFilter, retry], {
    queryFn: async () => {
      if (!options.showLogs || !searchbarFilter || !searchbarFilter.orgId || !user) {
        return Promise.resolve([]);
      }

      const { items } = await getMachineLogsV2(org!.id, user!.id, options.criteria, searchbarFilter.facilityId, searchbarFilter.machineId);
      return items;
    },
    refetchOnMount: 'always',
  });

  const {
    isLoading: testResultsLoading,
    isError: testResultsError,
    data: testResults,
  } = useQuery(["home/widget/tracker/test-results", options, org, searchbarFilter, retry], {
    queryFn: async () => {
      if (!options.showTestResults || !org) {
        return Promise.resolve([]);
      }

      const r = await getWidgetTestResults(org.id, searchbarFilter.machineId ? { machineId: searchbarFilter.machineId } : searchbarFilter.facilityId ? { facilityId: searchbarFilter.facilityId } : { orgId: org.id }, {
        limit: 10000,
        ...options.criteria,
      });
      // Not filtered by perform permission - sign-off / approve are gated per result instead.
      return r.items;
    },
    refetchOnMount: 'always',
  });

  const {
    isLoading: eventsLoading,
    isError: eventsError,
    data: events,
  } = useQuery(["home/widget/calendar/events", options, searchbarFilter, retry], {
    queryFn: async () => {
      if (!options.showSchedule || !searchbarFilter || !searchbarFilter.orgId) {
        return Promise.resolve([]);
      }

      const start = options.criteria.start;
      const machineId = machine ? machine.id : undefined;
      const facilityId = facility ? facility.id : undefined;

      const { items } = await getMachineSchedule(org!.id, { start: start, machineId: machineId, facilityId: facilityId });
      return items.filter((b) => b.active);
    },
    refetchOnMount: 'always',
  });

  // Handles async input changes with delay
  useStatusEffect(
    (status) => {
      // Skip first render as we only want to eval this effect when input changes actually happen
      if (status.current === "mount") {
        return;
      }

      const { delay, ...rest } = inputCriteria;

      // Delay setting value to limit api calls for every change
      const delayTimer = setTimeout(() => {
        if (status.current !== "unmount") {
          // close filter menu
          setMenuAnchorEl(null);
          // Set criteria for API
          setCalendarOptions((prev) => ({ ...prev!, ...rest }));
        }
      }, delay);

      // this will clear Timeout when inputCriteria changes
      return () => {
        clearTimeout(delayTimer);
      };
    },
    [inputCriteria, setCalendarOptions]
  );

  const handleCriteriaChange = useCallback(
    (change: Partial<typeof inputCriteria>, delay = 0) =>
      () => {
        setInputCriteria((prev) => {
          return { ...prev, ...change, criteria: { ...prev.criteria, ...change.criteria }, delay };
        });
      },
    []
  );

  const handleCalendarDateChange = useCallback(
    (date: MaterialUiPickersDate) => {
      setSelectedDate(moment(date).format(DATE_FORMAT));
    },
    [setSelectedDate]
  );

  const handleCalendarMonthChange = useCallback(
    (date: MaterialUiPickersDate) => {
      setSelectedDate(moment(date).format(DATE_FORMAT));

      const start = moment(date).startOf("month").unix();
      const end = moment(date).endOf("month").unix();

      handleCriteriaChange(
        {
          criteria: {
            start,
            end,
          },
        },
        500
      )();
    },
    [handleCriteriaChange, setSelectedDate]
  );

  const handleRunTests = useCallback(
    (tests: MachineTest[]) => {
      history.push(`/tests/runs/${tests[0].machineId}`, {
        previousLocation: { pathname: `/` },
        tests: tests,
      });
    },
    [history]
  );

  const handleViewLog = useCallback(
    (log: MachineLog) => {
      history.push(`/logs/${log.id}`, {
        previousLocation: { pathname: `/` },
        log: log,
      });
    },
    [history]
  );

  const handleViewEvent = useCallback(
    (event: MachineEvent) => {
      history.push(`/events/${event.id}`, {
        previousLocation: { pathname: `/` },
        event,
      });
    },
    [history]
  );

  // Same rule as the Events list: opening an event requires schedule edit for its facility.
  const disableMachineEvent = useCallback(
    (event: MachineEvent) => !hasPermission(Permission.scheduleEdit, { facilityId: event.machine.facilityId ?? event.machine.facility?.id }),
    [hasPermission]
  );

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setMenuAnchorEl(event.currentTarget);
    },
    [setMenuAnchorEl]
  );

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, [setMenuAnchorEl]);

  const openCalendarTestList = useCallback(
    async ({ name, tests, status }: { name: string; tests: MachineTest[]; status?: StatusType }) => {
      const requestId = ++listRequestId.current;

      // A compliance-grid row (status set) describes the selected day's cycle for one machine +
      // frequency. Mark each test from that day's runs: run -> Complete, only skipped runs ->
      // Skipped, no run -> the row's own status (Missed / Incomplete / Pending ...).
      const statuses: Record<string, StatusType> | undefined =
        status && tests.length ? Object.fromEntries(tests.map((t) => [t.id, status])) : undefined;

      // Open the list right away; the per-test statuses fill in once that day's runs load.
      setSelectedTests({ name, tests, statuses });
      setShowCalendarTestList(true);

      if (history.location.hash !== CALENDAR_TEST_LIST_HASH) {
        history.push(`/${CALENDAR_TEST_LIST_HASH}`);
      }

      // A Missed row is synthesized for a day with no runs, so there is nothing to look up.
      if (statuses && status !== "missed" && org) {
        try {
          setListLoading(true);
          const r = await getDeviceTestResults(
            org.id,
            { machineId: tests[0].machineId },
            {
              ...options.criteria,
              limit: 10000,
              // UTC day, matching the grid's DATE(test_date) bucketing.
              start: moment.utc(selectedDate).startOf("day").unix(),
              end: moment.utc(selectedDate).endOf("day").unix(),
            }
          );

          const runStatusesByTestId = new Map<number, string[]>();
          for (const run of r.items) {
            const testId = run.testVersion?.testId;
            if (run.exclude || testId == null) {
              continue;
            }
            runStatusesByTestId.set(testId, [...(runStatusesByTestId.get(testId) ?? []), run.status]);
          }

          // Another row was opened while this one was loading - drop the stale response.
          if (requestId !== listRequestId.current) {
            return;
          }

          const updated = { ...statuses };
          for (const t of tests) {
            const runStatuses = runStatusesByTestId.get(t.testId);
            if (runStatuses?.length) {
              updated[t.id] = runStatuses.every((s) => s === TestRunStatus.notRun) ? "skipped" : "complete";
            }
          }
          setSelectedTests({ name, tests, statuses: updated });
        } catch (error) {
          // Keep the row's status on every test rather than failing the list.
          console.error(error);
        } finally {
          if (requestId === listRequestId.current) {
            setListLoading(false);
          }
        }
      }
    },
    [history, setSelectedTests, org, getDeviceTestResults, options.criteria, selectedDate]
  );

  const handleOpenTestResultList = useCallback(
    async (selectedTestResults: { name: string; id: number; frequency?: number }) => {
      const requestId = ++listRequestId.current;

      // Open the list right away with a loader; the runs fill in once they load.
      setSelectedTestResults({ name: selectedTestResults.name, testResults: [] });
      setShowCalendarTestResultList(true);

      if (history.location.hash !== CALENDAR_TEST_RESULT_LIST_HASH) {
        history.push(`/${CALENDAR_TEST_RESULT_LIST_HASH}`);
      }

      const flattenedOptions = {
        ...options.criteria,
        limit: 10000,
        // UTC day, matching how the calendar buckets results and grid rows (moment.utc(testDate) /
        // the API's DATE(test_date)) - a local-day window misses runs across the UTC midnight.
        start: moment.utc(selectedDate).startOf("day").unix(),
        end: moment.utc(selectedDate).endOf("day").unix(),
      };

      try {
        setListLoading(true);
        const r = await getDeviceTestResults(org!.id, { machineId: selectedTestResults.id }, flattenedOptions);

        // Another row was opened while this one was loading - drop the stale response.
        if (requestId !== listRequestId.current) {
          return;
        }

        setSelectedTestResults({
          name: selectedTestResults.name,
          testResults: r.items.filter(test => (selectedTestResults.frequency ? selectedTestResults.frequency == test.setup.frequency : true))
        });
      } catch (error) {
        console.error(error);
        if (requestId === listRequestId.current) {
          setAlert({ severity: "error", messages: ["Could not load test results"] });
        }
      } finally {
        if (requestId === listRequestId.current) {
          setListLoading(false);
        }
      }
    },
    [options.criteria, selectedDate, getDeviceTestResults, org, setSelectedTestResults, history, setAlert]
  );

  const handleViewTestResult = useCallback(
    (testResult: MachineTestResult) => {
      history.push(`/tests/${testResult.testVersion.testId}/results/${testResult.id}`, {
        previousLocation: `/${CALENDAR_TEST_RESULT_LIST_HASH}`,
        testResult: testResult,
        machine: {
          machineId: testResult.machine.id,
          facilityId: testResult.machine.facility!.id,
          name: testResult.machine.name,
        },
      });
    },
    [history]
  );

  const handleViewTestResults = useCallback(
    (test: MachineTest) => {
      history.push(`/tests/${test.testId}/results`, test);
    },
    [history]
  );

  const handleViewTestGraph = useCallback(
    (test: MachineTest) => {
      history.push(`/tests/${test.testId}/graph`, test);
    },
    [history]
  );

  const handleViewTestSetup = useCallback(
    (test: MachineTest) => {
      history.push(`/tests/${test.testId}`, test);
    },
    [history]
  );

  const handleSignoff = useCallback(
    (selectedTestResults: MachineTestResult[]) => {
      const ids = selectedTestResults.map((t) => t.id);

      setLoading(true);
      signTestResultsV2(org!.id, ids)
        .then(() => {
          setAlert({ severity: "success", messages: [`Signed off on test result${ids.length ? "s" : ""}`] });
          handleCriteriaChange({}, 0)();
          setRetry((prev) => prev + 1);
        })
        .catch((error) => {
          console.error(error);
          setAlert({ severity: "error", messages: [`Could not sign off on test result${ids.length ? "s" : ""}`] });
        })
        .finally(() => {
          setLoading(false);
          setShowCalendarTestResultList(false);
        });
    },
    [handleCriteriaChange, org, setAlert, signTestResultsV2]
  );

  const handleApprove = useCallback(
    (selectedTestResults: MachineTestResult[]) => {
      const ids = selectedTestResults.map((t) => t.id);

      setLoading(true);
      approveTestResultsV2(org!.id, ids)
        .then(() => {
          setAlert({ severity: "success", messages: [`Approved test result${ids.length ? "s" : ""}`] });
          handleCriteriaChange({}, 0)();
          setRetry((prev) => prev + 1);
        })
        .catch((error) => {
          console.error(error);
          setAlert({ severity: "error", messages: [`Could not approve test result${ids.length ? "s" : ""}`] });
        })
        .finally(() => {
          setLoading(false);
          setShowCalendarTestResultList(false);
        });
    },
    [approveTestResultsV2, handleCriteriaChange, org, setAlert]
  );

  const handleCloseDrawer = useCallback(
    (hash: string) => (event?: React.KeyboardEvent | React.MouseEvent | {}) => {
      if (
        event != null &&
        (event as React.KeyboardEvent | React.MouseEvent).type === "keydown" &&
        ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
      ) {
        return;
      }

      if (hash === CALENDAR_TEST_LIST_HASH) {
        setShowCalendarTestList(false);
      } else if (hash === CALENDAR_TEST_RESULT_LIST_HASH) {
        setShowCalendarTestResultList(false);
      }

      if (history.location.hash === hash) {
        // This will trigger our history listener to close the dialog
        history.push("/");
      }
    },
    [history]
  );

  const handleToday = useCallback(() => {
    handleCalendarMonthChange(moment().startOf("day"));
  }, [handleCalendarMonthChange]);

  const handleRetry = useCallback(() => {
    setRetry((prev) => prev + 1);
  }, []);

  const MemoCalendar = useMemo(
    () => (
      <Calendar
        value={moment(selectedDate || moment())}
        onValueChange={handleCalendarDateChange}
        onMonthChange={handleCalendarMonthChange}
        onSelectMachineTests={openCalendarTestList}
        onSelectMachineTestResults={handleOpenTestResultList}
        onSelectMachineLog={handleViewLog}
        onSelectMachineEvent={handleViewEvent}
        disableMachineEvent={disableMachineEvent}
        tests={tests?.data || []}
        testsTotal={tests?.total || 0}
        complianceGrid={complianceGrid || []}
        events={events || []}
        logs={logs || []}
        testResults={testResults || []}
        dateFormat={`${DATE_FORMAT}`}
      />
    ),
    [selectedDate, handleCalendarDateChange, handleCalendarMonthChange, openCalendarTestList, handleOpenTestResultList, handleViewLog, handleViewEvent, disableMachineEvent, tests?.data, tests?.total, complianceGrid, events, logs, testResults]
  );

  const disableEditTestIds = useMemo(
    () => tests?.data?.filter((i) => !hasPermission(Permission.adminEditTests, { facilityId: i.facilityId })).map((i) => i.id) || [],
    [hasPermission, tests]
  );

  const disableRunTestIds = useMemo(() => {
    const items = tests?.data?.filter((i) => {
      const perm =
        i.frequency === 1
          ? Permission.dailyPerform
          : i.frequency === 7 || i.frequency === 14
            ? Permission.weeklyPerform
            : i.frequency === 30
              ? Permission.monthlyPerform
              : i.frequency === 91
                ? Permission.quarterlyPerform
                : i.frequency === 183 || i.frequency === 365 || i.frequency === 730
                  ? Permission.yearlyPerform
                  : undefined;

      const hasPerm = perm && hasPermission(perm, { facilityId: i.facilityId, deviceTypeId: i.machineTypeId });
      return !hasPerm;
    });

    return items?.map((i) => i.id) || [];
  }, [hasPermission, tests]);

  const disableSignTestIds = useMemo(() => {
    const items = testResults?.filter((i) => {
      const perm =
        i.frequency === 1
          ? Permission.dailySignoff
          : i.frequency === 7 || i.frequency === 14
            ? Permission.weeklySignoff
            : i.frequency === 30
              ? Permission.monthlySignoff
              : i.frequency === 91
                ? Permission.quarterlySignoff
                : i.frequency === 183 || i.frequency === 365 || i.frequency === 730
                  ? Permission.yearlySignoff
                  : undefined;

      const hasPerm =
        perm &&
        hasPermission(perm, {
          facilityId: i.facilityId,
          deviceTypeId: i.machineTypeId,
        });
      return !hasPerm;
    });

    return items?.map((i) => i.id) || [];
  }, [hasPermission, testResults]);

  const disableApproveTestIds = useMemo(() => {
    const items = testResults?.filter((i) => {
      const perm =
        i.frequency === 1
          ? Permission.dailyApprove
          : i.frequency === 7 || i.frequency === 14
            ? Permission.weeklyApprove
            : i.frequency === 30
              ? Permission.monthlyApprove
              : i.frequency === 91
                ? Permission.quarterlyApprove
                : i.frequency === 183 || i.frequency === 365 || i.frequency === 730
                  ? Permission.yearlyApprove
                  : undefined;

      const hasPerm =
        perm &&
        hasPermission(perm, {
          facilityId: i.facilityId,
          deviceTypeId: i.machineTypeId,
        });
      return !hasPerm;
    });

    return items?.map((i) => i.id) || [];
  }, [hasPermission, testResults]);

  const MemoTestListDrawer = useMemo(() => {
    const viewItemAction = {
      onClick: handleViewTestSetup,
      disabled: (item: MachineTest) => disableEditTestIds.includes(item.id),
      disabledText: () => notifications.action.noPermission,
    };

    const disableRunTests = (selectedItems: MachineTest[]) => {
      const selectedIds = selectedItems.map((i) => i.id);
      return selectedIds.findIndex((i) => disableRunTestIds.includes(i)) >= 0;
    };

    const disableRunTestsText = (selectedItems: MachineTest[]) => {
      const macId = selectedItems[0]?.machineId;
      const selectedIds = selectedItems.map((i) => i.id);
      const noPermission = selectedIds.findIndex((i) => disableRunTestIds.includes(i)) >= 0;

      if (noPermission) {
        return notifications.action.noPermission;
      }

      if (selectedItems.findIndex((i) => i.status !== "ready") >= 0) {
        return notifications.test.notReady;
      }

      if (selectedItems.findIndex((i) => i.machineId !== macId) >= 0) {
        return notifications.test.noMultiMachineRuns;
      }

      return "";
    };

    const runTestsAction = {
      onClick: handleRunTests,
      disabled: disableRunTests,
      disabledText: disableRunTestsText,
    };

    return (
      <Drawer
        anchor="bottom"
        open={showCalendarTestList}
        onClose={handleCloseDrawer(CALENDAR_TEST_LIST_HASH)}
        PaperProps={{
          style: { top: "10%" },
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        <PageContainer>
          <TestList
            id="test-list"
            header={`${selectedTests?.name} - Tests`}
            items={selectedTests?.tests || []}
            total={selectedTests?.tests?.length || 0}
            pageLimits={environment.pageLimits}
            // Judge the Status column as of the calendar day the user opened this list from, so
            // it matches the row they clicked (a test due on that day reads "Due" there).
            statusAsOfDate={selectedDate || undefined}
            // Same Calendar-only status rules as the tiles/list (past due -> "Overdue").
            statusOptions={CALENDAR_STATUS_OPTIONS}
            // A compliance-grid row (Missed / Incomplete / Pending / Skipped) describes a past
            // cycle - show that status rather than one re-derived from each test's current dueAt.
            statusOverrides={selectedTests?.statuses}
            hidePendingColumn
            onViewTestResults={handleViewTestResults}
            viewItemAction={viewItemAction}
            onViewTestGraph={handleViewTestGraph}
            runTestsAction={runTestsAction}
            actions={
              <Grid container justify="flex-end" spacing={2}>
                <Grid item>
                  <Link
                    component={IconButton}
                    color="textSecondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${environment.userManualBaseUrl}/TestList.pdf`}
                    aria-label="test list help"
                  >
                    <HelpOutline />
                  </Link>

                  <IconButton edge="right" aria-label="close list" onClick={handleCloseDrawer(CALENDAR_TEST_LIST_HASH)}>
                    <Close />
                  </IconButton>
                </Grid>
              </Grid>
            }
          ></TestList>
        </PageContainer>
        {showCalendarTestList && (loading || listLoading) && <PageLoader opacity="0.5" position="fixed" />}
      </Drawer>
    );
  }, [
    handleViewTestSetup,
    handleRunTests,
    showCalendarTestList,
    handleCloseDrawer,
    selectedTests?.name,
    selectedTests?.tests,
    selectedTests?.statuses,
    selectedDate,
    handleViewTestResults,
    handleViewTestGraph,
    loading,
    listLoading,
    disableEditTestIds,
    disableRunTestIds,
  ]);

  // The result list is loaded per day and is not filtered by permission, so check each result
  // directly as well as by id against the widget feed.
  const hasResultPermission = useCallback(
    (result: MachineTestResult, action: "Signoff" | "Approve") => {
      const frequency = result.setup.frequency;
      const period =
        frequency === 1
          ? "daily"
          : frequency === 7 || frequency === 14
            ? "weekly"
            : frequency === 30
              ? "monthly"
              : frequency === 91
                ? "quarterly"
                : frequency === 183 || frequency === 365 || frequency === 730
                  ? "yearly"
                  : undefined;

      return (
        !!period &&
        hasPermission(`${period}${action}` as Permission, {
          facilityId: result.machine.facilityId,
          deviceTypeId: result.machine.machineType.id,
        })
      );
    },
    [hasPermission]
  );

  const MemoTestResultListDrawer = useMemo(() => {
    const disableSignTests = (selectedItems: MachineTestResult[]) => {
      const selectedIds = selectedItems.map((i) => i.id);
      return selectedIds.findIndex((i) => disableSignTestIds.includes(i)) >= 0 || selectedItems.some((i) => !hasResultPermission(i, "Signoff"));
    };

    const disableSignTestsText = (selectedItems: MachineTestResult[]) => {
      if (disableSignTests(selectedItems)) {
        return notifications.action.noPermission;
      }

      return "";
    };

    const signTestsAction = {
      onClick: handleSignoff,
      disabled: disableSignTests,
      disabledText: disableSignTestsText,
    };

    const disableApproveTests = (selectedItems: MachineTestResult[]) => {
      const selectedIds = selectedItems.map((i) => i.id);
      return selectedIds.findIndex((i) => disableApproveTestIds.includes(i)) >= 0 || selectedItems.some((i) => !hasResultPermission(i, "Approve"));
    };

    const disableApproveTestsText = (selectedItems: MachineTestResult[]) => {
      if (disableApproveTests(selectedItems)) {
        return notifications.action.noPermission;
      }

      return "";
    };

    const approveTestsAction = {
      onClick: handleApprove,
      disabled: disableApproveTests,
      disabledText: disableApproveTestsText,
    };

    return (
      <Drawer
        anchor="bottom"
        open={showCalendarTestResultList}
        onClose={handleCloseDrawer(CALENDAR_TEST_RESULT_LIST_HASH)}
        PaperProps={{
          style: { top: "10%" },
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        <PageContainer>
          <TestResultList
            id="test-result-list"
            header={`${selectedTestResults?.name} - Test Results`}
            items={selectedTestResults?.testResults || []}
            total={selectedTestResults?.testResults?.length || 0}
            onViewItem={handleViewTestResult}
            pageLimits={environment.pageLimits}
            signTestsAction={signTestsAction}
            approveTestsAction={approveTestsAction}
            hideLocation={!selectedTestResults?.testResults[0]?.machine.mobile}
            hideFacility={selectedTestResults?.testResults[0]?.machine.mobile}
            actions={
              <Grid container justify="flex-end" spacing={2}>
                <Grid item>
                  <Link
                    component={IconButton}
                    color="textSecondary"
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`${environment.userManualBaseUrl}/ResultsList.pdf`}
                    aria-label="result list help"
                  >
                    <HelpOutline />
                  </Link>
                  <IconButton edge="right" aria-label="close list" onClick={handleCloseDrawer(CALENDAR_TEST_RESULT_LIST_HASH)}>
                    <Close />
                  </IconButton>
                </Grid>
              </Grid>
            }
          ></TestResultList>
        </PageContainer>
        {showCalendarTestResultList && (loading || listLoading) && <PageLoader opacity="0.5" position="fixed" />}
      </Drawer>
    );
  }, [handleSignoff, handleApprove, showCalendarTestResultList, handleCloseDrawer, selectedTestResults?.name, selectedTestResults?.testResults, handleViewTestResult, loading, listLoading, disableSignTestIds, disableApproveTestIds, hasResultPermission]);

  const { onRemoveWidget, ...rest } = props;
  const defaultWidgetCardProps = {
    ...rest,
  };

  const widgetMenuButtonRef = useRef(null);

  return (
    <WidgetCard
      id="calendar-widget"
      header="Calendar"
      loading={loading || testsLoading || testResultsLoading || eventsLoading || complianceGridLoading}
      {...defaultWidgetCardProps}
      actions={
        <>
          <Grid container>
            <Grid item>
              <Box paddingRight={1}>
                <Button variant="outlined" onClick={handleToday}>
                  Today
                </Button>
              </Box>
            </Grid>
            <Grid item>
              <CalendarLegendPopup
                trigger={<Button variant="outlined">Legend</Button>}
              />
            </Grid>
            <Grid item>
              <Link
                component={IconButton}
                color="textSecondary"
                target="_blank"
                rel="noopener noreferrer"
                href={`${environment.userManualBaseUrl}/Calendar.pdf`}
                aria-label="calendar help"
              >
                <HelpOutline />
              </Link>
            </Grid>

            {onRemoveWidget != null && (
              <Grid item ref={widgetMenuButtonRef}>
                <IconButton id="calendar-widget-menu-button" aria-label="calendar widget menu" aria-haspopup="true" onClick={handleOpenMenu}>
                  <MoreVert />
                </IconButton>
                <Menu
                  id="calendar-menu"
                  keepMounted
                  anchorEl={widgetMenuButtonRef.current}
                  open={Boolean(menuAnchorEl?.id === "calendar-widget-menu-button")}
                  onClose={handleCloseMenu}
                  disableScrollLock={true}
                  container={widgetMenuButtonRef.current}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <MenuItem button onClick={onRemoveWidget}>
                    <ListItemIcon style={{ marginLeft: "-8px", marginRight: "8px" }}>
                      <RemoveCircleOutline />
                    </ListItemIcon>
                    <ListItemText primary="Remove widget" />
                  </MenuItem>
                </Menu>
              </Grid>
            )}
          </Grid>
        </>
      }
    >
      {testsError || testResultsError || eventsError ? (
        <RetryWidget onRetry={handleRetry} />
      ) : (
        <>
          {MemoCalendar}
          {MemoTestListDrawer}
          {MemoTestResultListDrawer}
        </>
      )}
    </WidgetCard>
  );
}

export default React.memo(CalendarWidget);
